import React, { useState } from "react";
import { Alert, Button, Table, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { IMsalContext } from "@azure/msal-react";
import { TransferExecution } from "./types";
import { fetchPastTransfers } from "./dataTransferApi";
import { formatTimestamp, RouteCell, StatusBadge } from "./display";

const { Text } = Typography;

const PAGE_SIZE = 20;

interface PastExecutionTabProps {
  spaceName: string;
  spaceApiName?: string | null;
  msalInstance: IMsalContext;
}

const PastExecutionTab: React.FC<PastExecutionTabProps> = ({
  spaceName,
  spaceApiName,
  msalInstance,
}) => {
  const queryClient = useQueryClient();
  const apiSpace = spaceApiName?.trim() || spaceName;
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  const {
    data,
    isFetching,
    isFetchingNextPage,
    isError,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ["dataTransfer", "past", apiSpace],
    queryFn: ({ pageParam }) =>
      fetchPastTransfers(msalInstance, apiSpace, {
        limit: PAGE_SIZE,
        cursor: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage => lastPage.next_cursor ?? undefined,
    staleTime: 30 * 1000,
  });

  const rows = data?.pages.flatMap(page => page.items) ?? [];
  const tableLoading = isFetching && !isFetchingNextPage;

  const toggleExpand = (key: string) =>
    setExpandedKeys(prev =>
      prev.includes(key) ? prev.filter(item => item !== key) : [...prev, key],
    );

  const columns: ColumnsType<TransferExecution> = [
    {
      title: "Route",
      key: "route",
      width: 260,
      ellipsis: true,
      render: (_, row) => (
        <RouteCell
          route={row.route}
          source={row.source_space}
          destination={row.destination_space}
          status={row.status}
        />
      ),
    },
    {
      title: "Execution ID",
      dataIndex: "execution_id",
      key: "execution_id",
      width: 170,
      ellipsis: true,
      render: (val: string) => (
        <Text className="dt-nowrap" style={{ fontSize: 13 }}>
          {val || "—"}
        </Text>
      ),
    },
    {
      title: "Mode",
      dataIndex: "mode",
      key: "mode",
      width: 110,
      render: (val: string) => (
        <Text className="dt-nowrap" style={{ fontSize: 13 }}>
          {val || "—"}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (val: string) => <StatusBadge status={val} />,
    },
    {
      title: "Started",
      dataIndex: "started_at",
      key: "started_at",
      width: 170,
      render: (val: string | null) => (
        <Text className="dt-nowrap" style={{ fontSize: 13 }}>
          {formatTimestamp(val)}
        </Text>
      ),
    },
    {
      title: "Completed",
      dataIndex: "completed_at",
      key: "completed_at",
      width: 170,
      render: (val: string | null) => (
        <Text className="dt-nowrap" style={{ fontSize: 13 }}>
          {formatTimestamp(val)}
        </Text>
      ),
    },
    {
      title: "",
      key: "error_action",
      width: 80,
      render: (_, row) => {
        if (!row.error_message) return <Text style={{ fontSize: 13 }}>—</Text>;
        const isOpen = expandedKeys.includes(row.execution_id);
        return (
          <Button
            type="link"
            size="small"
            style={{ padding: 0, color: isOpen ? "#595959" : "#ff4d4f" }}
            onClick={() => toggleExpand(row.execution_id)}
          >
            {isOpen ? "Hide" : "Error"}
          </Button>
        );
      },
    },
  ];

  return (
    <div className="dt-tab-body">
      {isError && (
        <Alert
          type="error"
          showIcon
          message="Failed to load past executions."
          style={{ marginBottom: 16 }}
        />
      )}

      <div className="dt-tab-toolbar">
        <Button
          icon={<ReloadOutlined />}
          onClick={() =>
            queryClient.resetQueries({
              queryKey: ["dataTransfer", "past", apiSpace],
            })
          }
          loading={tableLoading}
        >
          Refresh
        </Button>
      </div>

      <Table<TransferExecution>
        className="dt-transfer-table"
        dataSource={rows}
        columns={columns}
        rowKey={row => row.execution_id}
        size="small"
        loading={tableLoading}
        pagination={false}
        locale={{ emptyText: "No past executions found." }}
        expandable={{
          expandedRowKeys: expandedKeys,
          showExpandColumn: false,
          expandedRowRender: row => (
            <Alert
              type="error"
              showIcon
              message="Error detail"
              description={row.error_message}
              style={{ margin: "4px 0" }}
            />
          ),
          rowExpandable: row => !!row.error_message,
        }}
      />
      {hasNextPage && (
        <div className="dt-load-more">
          <Button onClick={() => fetchNextPage()} loading={isFetchingNextPage}>
            Load more
          </Button>
        </div>
      )}
    </div>
  );
};

export default PastExecutionTab;
